import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { FiX } from 'react-icons/fi';

const DEFAULT_SERVICES = [
    {
        title: 'Intimate Services',
        items: [
            'Vaginal Rejuvination',
            'Post Delivery Pelvic Floor Rehabilitation',
            'Urinary Bladder Care',
            'Cervical Rejuvination',
            'Vulval Rejuvination'
        ]
    },
    {
        title: 'Laser & Advanced Procedures',
        items: [
            'CO2 Laser',
            'Nd:YAG Lazer',
            'Diode Lazer',
            'Lyper pigmentation treatment',
            'Diode laser',
            'Plasma Lazer'
        ]
    },
    {
        title: 'Advanced Skin & Aesthetics',
        items: [
            'Chemical Peels',
            'Botox & Injectables',
            'Fillers',
            'Exosomes',
            'PRP (Platelet Rich Plasma)',
            'GFC (Growth Factor Concentrate)',
            'Microneedling'
        ]
    },
    {
        title: 'Medical Dermatology',
        categories: [
            {
                name: 'A. Skin Infection',
                items: [
                    'Fungal Infection',
                    'Bacterial Infection',
                    'Viral',
                    'Parasite Infection',
                    'Bullous Disorder'
                ]
            },
            {
                name: 'B. Pigmentary Disorder',
                items: [
                    '1. Hyper Pigmentation',
                    'Melasma',
                    'Freckles',
                    'Naevus',
                    'Acanthosis',
                    'Nigricans',
                    'Tanning',
                    '2. Hypo Pigmentation',
                    'Vitiligo',
                    'IGH',
                    'Mydriasis Alba'
                ]
            },
            {
                name: 'C. Acne & Acne Scar Management',
                items: []
            }
        ]
    },
    {
        title: 'Hair & Scalp Care',
        items: [
            'Androgenic Alopecia',
            'Alopecia Areata',
            'Male Pattern Hair Loss',
            'Female Pattern Hair Loss'
        ]
    }
];

const Services = () => {
    const navigate = useNavigate();
    const [expandedCategories, setExpandedCategories] = useState({});
    const [services, setServices] = useState(DEFAULT_SERVICES);
    const [selectedItem, setSelectedItem] = useState(null);

    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, 'services'), (snapshot) => {
            if (!snapshot.empty) {
                const dynamicServices = snapshot.docs.map(doc => doc.data());
                // Sort by order
                dynamicServices.sort((a, b) => (a.order || 0) - (b.order || 0));
                setServices(dynamicServices);
            }
        }, (error) => {
            console.error("Error fetching services:", error);
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, []);

    // Create a mapping of service titles to IDs for navigation
    const serviceIdMap = {
        'Intimate & Pelvic Health Care': 'vulvovaginal-conditions',
        'Laser & Advanced Procedures': 'laser-energy-treatments',
        'Advanced Skin & Aesthetic Treatments': 'injectables-aesthetics',
        'Medical Dermatology': 'skin-infections-medical',
        'Hair & Scalp Care': 'hair-scalp-treatments'
    };

    const handleCardClick = (serviceTitle) => {
        const serviceId = serviceIdMap[serviceTitle];
        if (serviceId) {
            navigate(`/services#${serviceId}`);
        }
    };

    const toggleCategory = (categoryKey, e) => {
        e.stopPropagation(); // Prevent card click navigation
        setExpandedCategories(prev => ({
            ...prev,
            [categoryKey]: !prev[categoryKey]
        }));
    };

    const handleItemClick = (e, item) => {
        // If item is an object with description, show modal
        if (typeof item === 'object' && (item.description || item.imageUrl)) {
            e.stopPropagation(); // Prevent card click
            setSelectedItem(item);
        } else {
            // Default behavior: do nothing (allow bubble up to card click if desired)
        }
    };

    const renderItemContent = (item) => {
        if (typeof item === 'string') return item;
        return item.name;
    };

    return (
        <section className="section" style={{ backgroundColor: '#fff5f5', padding: '100px 0', position: 'relative' }}>
            <div className="container">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 style={{
                        fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                        fontFamily: 'var(--font-heading)',
                        color: 'var(--secondary)',
                        marginBottom: '60px',
                        fontWeight: '700',
                        textAlign: 'center'
                    }}>
                        SERVICES
                    </h2>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '40px'
                    }}>
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                style={{
                                    padding: '30px',
                                    backgroundColor: 'var(--white)',
                                    borderRadius: '12px',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#faf8f8';
                                    e.currentTarget.style.transform = 'translateY(-8px)';
                                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(181, 131, 131, 0.2)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--white)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                                }}
                                onClick={() => handleCardClick(service.title)}
                            >
                                <h3 style={{
                                    fontSize: '1.3rem',
                                    color: 'var(--secondary)',
                                    marginBottom: '20px',
                                    fontWeight: '700',
                                    fontFamily: 'var(--font-heading)'
                                }}>
                                    {service.title}
                                </h3>

                                <div style={{
                                    width: '40px',
                                    height: '3px',
                                    background: 'linear-gradient(90deg, var(--primary), #d4a5a5)',
                                    marginBottom: '20px'
                                }}></div>

                                <div style={{ flex: 1 }}>
                                    {service.categories && service.categories.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                            {service.categories.map((category, catIndex) => (
                                                <div key={catIndex}>
                                                    <div
                                                        onClick={(e) => toggleCategory(`${index}-${catIndex}`, e)}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            cursor: 'pointer',
                                                            padding: '8px 0',
                                                            borderBottom: catIndex !== service.categories.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none'
                                                        }}
                                                    >
                                                        <span style={{
                                                            fontWeight: '600',
                                                            color: 'var(--secondary)',
                                                            fontSize: '1rem'
                                                        }}>
                                                            {category.name}
                                                        </span>
                                                        <span style={{
                                                            transform: expandedCategories[`${index}-${catIndex}`] ? 'rotate(180deg)' : 'rotate(0deg)',
                                                            transition: 'transform 0.3s ease',
                                                            color: 'var(--primary)',
                                                            fontSize: '0.8rem'
                                                        }}>
                                                            ▼
                                                        </span>
                                                    </div>
                                                    <AnimatePresence>
                                                        {expandedCategories[`${index}-${catIndex}`] && (
                                                            <motion.ul
                                                                initial={{ height: 0, opacity: 0 }}
                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                exit={{ height: 0, opacity: 0 }}
                                                                transition={{ duration: 0.3 }}
                                                                style={{
                                                                    listStyle: 'none',
                                                                    padding: '10px 0 0 10px',
                                                                    margin: 0,
                                                                    overflow: 'hidden'
                                                                }}
                                                            >
                                                                {category.items.map((item, itemIndex) => (
                                                                    <li
                                                                        key={itemIndex}
                                                                        onClick={(e) => handleItemClick(e, item)}
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'flex-start',
                                                                            gap: '10px',
                                                                            marginBottom: '8px',
                                                                            color: 'var(--text-light)',
                                                                            fontSize: '0.9rem',
                                                                            lineHeight: '1.5',
                                                                            cursor: (typeof item === 'object' && item.description) ? 'pointer' : 'inherit'
                                                                        }}
                                                                    >
                                                                        <span style={{ color: 'var(--primary)', marginTop: '4px', fontSize: '0.7rem' }}>●</span>
                                                                        <span style={{
                                                                            textDecoration: 'none',
                                                                            transition: 'color 0.2s ease',
                                                                        }}
                                                                            onMouseEnter={(e) => e.target.style.color = 'var(--primary)'}
                                                                            onMouseLeave={(e) => e.target.style.color = 'inherit'}
                                                                        >
                                                                            {renderItemContent(item)}
                                                                        </span>
                                                                    </li>
                                                                ))}
                                                            </motion.ul>
                                                        )}
                                                    </AnimatePresence>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <ul style={{
                                            listStyle: 'none',
                                            padding: 0,
                                            margin: 0
                                        }}>
                                            {service.items && service.items.map((item, itemIndex) => (
                                                <li
                                                    key={itemIndex}
                                                    onClick={(e) => handleItemClick(e, item)}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: '12px',
                                                        marginBottom: '12px',
                                                        color: 'var(--text-light)',
                                                        fontSize: '0.95rem',
                                                        lineHeight: '1.6',
                                                        cursor: (typeof item === 'object' && item.description) ? 'pointer' : 'inherit'
                                                    }}
                                                >
                                                    <span style={{
                                                        color: 'var(--primary)',
                                                        fontSize: '1.2rem',
                                                        lineHeight: '1',
                                                        marginTop: '2px'
                                                    }}>•</span>
                                                    <span style={{
                                                        textDecoration: 'none',
                                                        transition: 'color 0.2s ease',
                                                    }}
                                                        onMouseEnter={(e) => e.target.style.color = 'var(--primary)'}
                                                        onMouseLeave={(e) => e.target.style.color = 'inherit'}
                                                    >
                                                        {renderItemContent(item)}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>

            {/* Modal for Item Details */}
            <AnimatePresence>
                {selectedItem && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedItem(null)}
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.6)',
                            zIndex: 1000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px',
                            backdropFilter: 'blur(5px)'
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                backgroundColor: 'white',
                                borderRadius: '24px',
                                width: '100%',
                                maxWidth: '600px',
                                maxHeight: '90vh',
                                overflow: 'auto',
                                position: 'relative',
                                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                            }}
                        >
                            <button
                                onClick={() => setSelectedItem(null)}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    right: '20px',
                                    background: 'rgba(0,0,0,0.05)',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '1.2rem',
                                    color: 'var(--text-dark)',
                                    zIndex: 10
                                }}
                            >
                                <FiX />
                            </button>

                            {selectedItem.imageUrl && (
                                <div style={{ width: '100%', height: '300px', overflow: 'hidden' }}>
                                    <img
                                        src={selectedItem.imageUrl}
                                        alt={selectedItem.name}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </div>
                            )}

                            <div style={{ padding: '40px' }}>
                                <h3 style={{
                                    fontFamily: 'var(--font-heading)',
                                    fontSize: '2rem',
                                    color: 'var(--secondary)',
                                    marginBottom: '20px'
                                }}>
                                    {selectedItem.name}
                                </h3>
                                <div style={{
                                    width: '60px',
                                    height: '4px',
                                    background: 'var(--primary)',
                                    marginBottom: '20px',
                                    borderRadius: '2px'
                                }}></div>
                                <p style={{
                                    fontSize: '1.1rem',
                                    lineHeight: '1.8',
                                    color: 'var(--text-light)',
                                    whiteSpace: 'pre-line'
                                }}>
                                    {selectedItem.description}
                                </p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Mobile Responsive Styles */}
            <style>{`
                @media (max-width: 768px) {
                    section[style*="padding: 100px 0"] {
                        padding: 60px 0 !important;
                    }

                    section h2 {
                        font-size: 2.5rem !important;
                        margin-bottom: 40px !important;
                    }

                    section div[style*="gridTemplateColumns"] {
                        grid-template-columns: 1fr !important;
                        gap: 25px !important;
                    }

                    section div[style*="padding: 30px"] {
                        padding: 25px !important;
                    }

                    section h3 {
                        font-size: 1.4rem !important;
                    }

                    section li {
                        font-size: 0.95rem !important;
                    }
                }
            `}</style>
        </section>
    );
};

export default Services;
